import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useStyletron } from 'styletron-react'
import type { DOMRectLike } from './TourSpotlight'
import type { TourPlacement } from './types'

// Focusable elements the hand-rolled trap cycles between.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
const CARD_MAX_WIDTH_PX = 360
// Above the scrim (998) and EditNavbar (1000) so the tour card is never hidden.
const CARD_Z_INDEX = 1001
// Breathing room between the card and the anchor it points at (> the spotlight's
// 8px cut-out padding, so the card never sits on the highlight ring).
const ANCHOR_GAP_PX = 12
// Minimum distance the card keeps from every viewport edge when it is clamped.
const VIEWPORT_MARGIN_PX = 16
// Used for the single pre-measurement frame only (the layout effect below
// replaces it with the real size before paint).
const ESTIMATED_CARD_HEIGHT_PX = 180

// The card owns its own dark (grey90) surface, matching the Figma tour frames.
const cardSurface = {
  backgroundColor: palette.grey90,
  padding: '20px',
  borderRadius: '8px',
  maxWidth: `${CARD_MAX_WIDTH_PX}px`,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.24)',
  outline: 'none',
}

interface CardSize {
  width: number
  height: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, Math.max(min, max)))

// Card position in VIEWPORT coordinates (the card is position:fixed, and the
// anchor rect comes from getBoundingClientRect — one coordinate system, so a
// scroll can never be counted twice). Clamped so a placement can't push the card
// off-screen; see ENG-976.
export const cardPosition = (
  anchorRect: DOMRectLike,
  placement: TourPlacement,
  size: CardSize
): { top: number; left: number } => {
  const anchorCenterX = anchorRect.left + anchorRect.width / 2
  const anchorCenterY = anchorRect.top + anchorRect.height / 2
  const centeredLeft = anchorCenterX - size.width / 2
  const centeredTop = anchorCenterY - size.height / 2

  let top: number
  let left: number
  switch (placement) {
    case 'top':
      top = anchorRect.top - size.height - ANCHOR_GAP_PX
      left = centeredLeft
      break
    case 'left':
      top = centeredTop
      left = anchorRect.left - size.width - ANCHOR_GAP_PX
      break
    case 'right':
      top = centeredTop
      left = anchorRect.left + anchorRect.width + ANCHOR_GAP_PX
      break
    case 'center':
      top = centeredTop
      left = centeredLeft
      break
    default:
      top = anchorRect.top + anchorRect.height + ANCHOR_GAP_PX
      left = centeredLeft
  }

  return {
    top: clamp(
      top,
      VIEWPORT_MARGIN_PX,
      window.innerHeight - size.height - VIEWPORT_MARGIN_PX
    ),
    left: clamp(
      left,
      VIEWPORT_MARGIN_PX,
      window.innerWidth - size.width - VIEWPORT_MARGIN_PX
    ),
  }
}

interface TourTooltipCardProps {
  // The spotlit control's live viewport rect (WizardTour measures it once and
  // shares it with the spotlight). null → the anchor is missing, or the step is
  // a modal notice: the card degrades to a viewport-centered dialog.
  anchorRect: DOMRectLike | null
  title: string
  body: string
  placement?: TourPlacement
  // Pre-translated dialog aria-label (the card is i18n-agnostic; the consumer,
  // WizardTour in U5, owns buildTranslate).
  dialogLabel: string
  onDismiss: () => void
  children?: React.ReactNode
}

// The active step's title/body rendered in a portaled, position:fixed card
// placed relative to the spotlighted control, with a hand-rolled focus trap +
// Esc-to-dismiss. It positions itself from the same viewport rect the spotlight
// uses instead of delegating to baseui's popper: the popper lays its body out in
// DOCUMENT coordinates, so pairing it with a viewport-coordinate anchor made the
// card drift by the scroll offset (ENG-976).
// When there is no anchor rect (the control isn't mounted / has vanished, or the
// step is a modal notice) it degrades to a CENTERED card (U6) so the step copy +
// pager still show and the tour never strands the user. i18n-agnostic (all copy/
// aria arrive pre-translated). NOT a React.FC.
export const TourTooltipCard = ({
  anchorRect,
  title,
  body,
  placement = 'bottom',
  dialogLabel,
  onDismiss,
  children,
}: TourTooltipCardProps): JSX.Element => {
  const [css] = useStyletron()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<Element | null>(null)
  const hasFocusedRef = useRef(false)
  const [cardSize, setCardSize] = useState<CardSize | null>(null)

  // Move focus into the card when it OPENS. A callback ref (not a mount effect)
  // because the card renders in a portal: this fires exactly when the dialog
  // node and its children are in the DOM.
  //
  //   context: it also fires on the centered-fallback ↔ anchored transition,
  //   where the card never leaves the screen but the node does. The two branches
  //   nest the dialog to different depths, so React type-mismatches the children,
  //   deletes the old subtree and rebuilds it. That splits into two cases, and
  //   they need opposite handling:
  //     - focus was OUT of the card (the merchant clicked a spotlit control
  //       through the pointer-transparent scrim): leave it alone. Re-taking it
  //       yanks them off that control, and re-capturing would record it as the
  //       tour's end-of-life restore target — which unmounts with the tour.
  //     - focus was IN the card: the swap deleted the focused node, so focus has
  //       fallen to <body>. Put it back, or nothing owns the keyboard —
  //       handleKeyDown is a React onKeyDown on the dialog, so Esc-to-dismiss and
  //       the Tab trap both go dead for the rest of the tour.
  //   The capture stays once-per-card-lifetime; only the focus WRITE may repeat,
  //   and only when focus was orphaned.
  //
  //   context: "was focus ours?" is read on ATTACH, not on detach. By the time
  //   the detach fires, React has already relocated focus to <body> (its
  //   prepareForCommit/restoreSelection pass runs before the mutation phase, and
  //   its restore target is the node being deleted), so a
  //   `detaching.contains(activeElement)` check there reads false in every case
  //   and silently never re-focuses.
  const setDialogNode = useCallback((node: HTMLDivElement | null) => {
    dialogRef.current = node
    if (!node) return
    if (previouslyFocusedRef.current === null) {
      previouslyFocusedRef.current = document.activeElement
    }
    // An orphaned activeElement is the fingerprint of the branch swap having
    // just destroyed whatever inside the card held focus. A merchant who clicked
    // a spotlit control through the scrim leaves activeElement pointing AT that
    // control, so they are left alone.
    const active = document.activeElement
    const focusOrphaned = !active || active === document.body
    if (hasFocusedRef.current && !focusOrphaned) return
    hasFocusedRef.current = true
    const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ;(focusables[0] ?? node).focus()
  }, [])

  // Restore focus on a GENUINE unmount only. The ref's detach branch also runs
  // on the anchored ↔ centered swap, where the card is still on screen — that is
  // the outward half of the focus bounce this removes.
  useEffect(() => {
    return () => {
      const prev = previouslyFocusedRef.current
      if (prev instanceof HTMLElement) prev.focus()
    }
  }, [])

  // Measure the rendered card so the placement math can center/flip against its
  // real size and clamp it inside the viewport. Deliberately runs on EVERY render
  // (the copy — and therefore the height — changes per step) but only commits
  // state when the size actually changed, so it cannot loop. useLayoutEffect (not
  // useEffect) so the corrected position lands before paint.
  useLayoutEffect(() => {
    const node = dialogRef.current
    if (!node) return
    const { width, height } = node.getBoundingClientRect()
    setCardSize(prev =>
      prev && prev.width === width && prev.height === height
        ? prev
        : { width, height }
    )
  })

  // Hand-rolled focus trap (no focus-lock lib is in-repo): Esc dismisses; Tab /
  // Shift+Tab cycle focus among the card's focusable elements, wrapping at the
  // ends. jsdom does not tab natively, so the handler moves focus explicitly.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onDismiss()
      return
    }
    if (event.key !== 'Tab') return
    const node = dialogRef.current
    if (!node) return
    const focusables = Array.from(
      node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    )
    if (focusables.length === 0) {
      event.preventDefault()
      return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    if (event.shiftKey) {
      if (active === first || !node.contains(active)) {
        event.preventDefault()
        last.focus()
      }
    } else if (active === last || !node.contains(active)) {
      event.preventDefault()
      first.focus()
    }
  }

  const cardContent = (
    <div
      ref={setDialogNode}
      role="dialog"
      // Intentionally NOT aria-modal: the tour is deliberately non-modal — the
      // scrim is pointer-transparent, and on the one interactive step (tour 2's
      // chat-input step, AI-1116) the spotlit control stays reachable through
      // it, so that control is not inert. aria-modal="true" would tell
      // assistive tech otherwise. (Every other step's control IS click-blocked
      // by TourSpotlight's DOM-containment document listener — this card is
      // never a descendant of the spotlit anchor, so it's naturally exempt —
      // but that's enforced there, not via aria-modal.) The Tab trap here is
      // hand-rolled and intentionally partial (role + label only).
      aria-label={dialogLabel}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={css(cardSurface)}
    >
      <Text.Header4 tag="div" bold color="spearmint50" className="mb-xs">
        {title}
      </Text.Header4>
      <Text.BodyText5 tag="p" color="white" className="mb-m">
        {body}
      </Text.BodyText5>
      {children}
    </div>
  )

  // No anchor rect (control not mounted / vanished, or a modal notice step):
  // degrade to a CENTERED card in a portal (U6). The same dialog content — role,
  // focus trap, Esc, pager children — still renders, so the tour stays usable.
  if (!anchorRect) {
    return createPortal(
      <div
        data-testid="tour-card-centered"
        className={css({
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: `${CARD_Z_INDEX}`,
          // Let clicks through to the page; the card re-enables them.
          pointerEvents: 'none',
        })}
      >
        <div className={css({ pointerEvents: 'auto' })}>{cardContent}</div>
      </div>,
      document.body
    )
  }

  const { top, left } = cardPosition(
    anchorRect,
    placement,
    cardSize ?? {
      width: CARD_MAX_WIDTH_PX,
      height: ESTIMATED_CARD_HEIGHT_PX,
    }
  )

  return createPortal(
    <div
      data-testid="tour-card-anchored"
      className={css({ position: 'fixed', zIndex: `${CARD_Z_INDEX}` })}
      // Live geometry from a getBoundingClientRect can't be a static css({}) —
      // the CLAUDE.md-sanctioned inline-style exemption for a computed rect.
      style={{ top, left }}
    >
      {cardContent}
    </div>,
    document.body
  )
}
